import {
  Alert,
  Box,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  Table,
  Typography,
} from '@mui/joy';
import { useAPI } from 'renderer/lib/authContext';

interface GpuInfo {
  // The token to pass to --accelerators, not necessarily what the provider's
  // own console calls the card.
  gpu: string;
  // The most of this GPU one request can ask for. Never stock on hand.
  count: number;
  // GPUs not currently allocated, where the provider genuinely reports it.
  // Undefined/null means "not known" and must never be rendered as 0.
  free?: number | null;
  common_names?: string[];
  source?: 'live' | 'catalog';
}

type Availability = 'live' | 'catalog' | 'mixed' | 'none';

/**
 * Whether the provider was actually asked what it is offering. This describes the
 * list of GPU *types*, not the quantities: the numbers mean the same thing under
 * either answer, so no caption here calls them stock. Answered per response rather
 * than assumed from the provider type, because the live providers fall back to
 * their catalog whenever a backend query fails.
 */
const AVAILABILITY_CAPTIONS: Record<Availability, string> = {
  live: 'Live availability — the provider was asked, and these are the types it is offering right now. A launch can still fail for lack of capacity.',
  catalog:
    'General catalog — the GPU types this provider knows how to launch, not checked against what it is offering right now.',
  mixed: 'Mixed — some types were checked live, others come from the catalog.',
  none: 'The provider did not say whether these types were checked live.',
};

interface ProviderGpusModalProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerName?: string;
}

/**
 * Read-only modal listing the GPU types a provider offers. The GPU column is the
 * token to type into an accelerator spec; the recognisable product names sit
 * beside it. The caption says whether the provider was asked live or is reciting
 * its catalog, because that is decided per request. Data comes from
 * GET /compute_provider/providers/{providerId}/gpus.
 */
export default function ProviderGpusModal({
  open,
  onClose,
  providerId,
  providerName,
}: ProviderGpusModalProps) {
  // Only fetch while open and with a provider selected (null params skip SWR).
  const { data, error, isLoading } = useAPI('compute_provider', ['gpus'], {
    providerId: open && providerId ? providerId : null,
  });

  const gpus: GpuInfo[] = Array.isArray(data?.gpus) ? data.gpus : [];
  const availability: Availability = data?.availability ?? 'none';
  const failed = Boolean(error) || data?.status === 'error';
  // Only Slurm reports what is unallocated, so elsewhere the column would be a
  // wall of em dashes.
  const hasFree = gpus.some((g) => g.free !== null && g.free !== undefined);
  const title = providerName
    ? `Available GPUs — ${providerName}`
    : 'Available GPUs';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 640, maxWidth: '95vw' }}>
        <ModalClose />
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : failed ? (
            <Alert color="danger" variant="soft">
              Failed to load GPUs for this provider.
            </Alert>
          ) : gpus.length === 0 ? (
            <Alert color="neutral" variant="soft">
              No GPU information available for this provider.
            </Alert>
          ) : (
            <>
              {/* Sits with the rows it describes: a caption above the ternary
                  would also print under the spinner and beside the error. */}
              <Typography level="body-sm" sx={{ mb: 1.5 }}>
                {AVAILABILITY_CAPTIONS[availability] ??
                  AVAILABILITY_CAPTIONS.none}
              </Typography>
              <Table size="sm" stripe="odd">
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>GPU</th>
                    <th>Common names</th>
                    <th style={{ width: 130, textAlign: 'right' }}>
                      Max per request
                    </th>
                    {hasFree ? (
                      <th style={{ width: 80, textAlign: 'right' }}>Free</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {gpus.map((g) => (
                    <tr key={g.gpu}>
                      <td>
                        <code>{g.gpu}</code>
                      </td>
                      <td>{g.common_names?.join(', ') || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{g.count}</td>
                      {hasFree ? (
                        <td style={{ textAlign: 'right' }}>
                          {/* Em dash, never 0: "not reported" is not "none free". */}
                          {g.free ?? '—'}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
              {hasFree ? (
                <Typography level="body-xs" sx={{ mt: 1 }}>
                  Free counts GPUs not currently allocated; — means the provider
                  does not report it.
                </Typography>
              ) : null}
            </>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}

ProviderGpusModal.defaultProps = {
  providerName: undefined,
};
